/**
 * Line Items Table Component
 * Displays invoice line items with TanStack Table
 * AC-2: Line Items Table
 */

import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CurrencyFormatter } from '@/components/common/CurrencyFormatter';
import { LineItemDetails } from '@/types';
import { useMemo } from 'react';

interface LineItemsTableProps {
  lineItems: LineItemDetails[];
}

export const LineItemsTable = ({ lineItems }: LineItemsTableProps) => {
  const columns = useMemo<ColumnDef<LineItemDetails>[]>(
    () => [
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => (
          <div className="font-medium">{row.original.description}</div>
        ),
      },
      {
        accessorKey: 'quantity',
        header: () => <div className="text-right">Qty</div>,
        cell: ({ row }) => (
          <div className="text-right">
            {row.original.quantity.toLocaleString('en-IN')}
          </div>
        ),
      },
      {
        accessorKey: 'unit',
        header: 'Unit',
        cell: ({ row }) => <div>{row.original.unit}</div>,
      },
      {
        accessorKey: 'unitPrice',
        header: () => <div className="text-right">Unit Price</div>,
        cell: ({ row }) => (
          <div className="text-right">
            <CurrencyFormatter amount={row.original.unitPrice} />
          </div>
        ),
      },
      {
        accessorKey: 'discount',
        header: () => <div className="text-right">Discount</div>,
        cell: ({ row }) => (
          <div className="text-right">
            <CurrencyFormatter amount={row.original.discount} />
            {row.original.discountPercentage > 0 && (
              <span className="text-xs text-muted-foreground ml-1">
                ({row.original.discountPercentage}%)
              </span>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'taxAmount',
        header: () => <div className="text-right">Tax</div>,
        cell: ({ row }) => (
          <div className="text-right">
            <CurrencyFormatter amount={row.original.taxAmount} />
            {row.original.taxRate > 0 && (
              <span className="text-xs text-muted-foreground ml-1">
                ({row.original.taxRate}%)
              </span>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'lineTotal',
        header: () => <div className="text-right">Total</div>,
        cell: ({ row }) => (
          <div className="text-right font-medium">
            <CurrencyFormatter amount={row.original.lineTotal} />
          </div>
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data: lineItems,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Line Items</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No line items found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
